import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useStore } from "../context/StoreContext";

export function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartSubtotal, cartCount } = useStore();
  const navigate = useNavigate();
  const freeShip = 150;
  const remaining = Math.max(0, freeShip - cartSubtotal);

  return (
    <>
      <div className={`fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 ${cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setCartOpen(false)} />
      <aside className={`fixed right-0 top-0 z-[70] h-full w-full max-w-md bg-bone shadow-2xl transition-transform duration-400 ease-out flex flex-col ${cartOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-hairline">
          <h3 className="font-serif text-2xl">Your Bag ({cartCount})</h3>
          <button onClick={() => setCartOpen(false)} data-testid="cart-close" aria-label="Close cart"><X size={22} strokeWidth={1.4} /></button>
        </div>

        {cart.length > 0 && (
          <div className="px-6 py-3 bg-beige/50 text-center overline text-[10px] text-charcoal">
            {remaining > 0 ? `You're $${remaining.toFixed(0)} away from free shipping` : "You've unlocked free shipping ✦"}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              <p className="font-serif text-2xl">Your bag is empty</p>
              <Link to="/shop" onClick={() => setCartOpen(false)} className="btn-primary" data-testid="cart-empty-shop">Shop Collection</Link>
            </div>
          ) : (
            <ul className="divide-y divide-hairline">
              {cart.map((item, i) => (
                <li key={i} className="flex gap-4 py-5" data-testid={`cart-item-${i}`}>
                  <img src={item.image} alt={item.name} className="h-28 w-24 object-cover bg-beige/40" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="font-serif text-lg leading-tight">{item.name}</Link>
                      <button onClick={() => removeFromCart(i)} data-testid={`cart-remove-${i}`} aria-label="Remove"><Trash2 size={15} strokeWidth={1.4} className="text-muted-foreground hover:text-destructive" /></button>
                    </div>
                    <p className="overline text-[10px] text-muted-foreground mt-1">{[item.color, item.size].filter(Boolean).join(" · ")}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-hairline">
                        <button onClick={() => updateQty(i, item.quantity - 1)} className="px-2 py-1" data-testid={`cart-dec-${i}`}><Minus size={13} /></button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button onClick={() => updateQty(i, item.quantity + 1)} className="px-2 py-1" data-testid={`cart-inc-${i}`}><Plus size={13} /></button>
                      </div>
                      <span className="font-display text-sm">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-hairline px-6 py-6 space-y-4">
            <div className="flex justify-between font-display text-sm">
              <span className="overline text-[11px]">Subtotal</span>
              <span data-testid="cart-subtotal">${cartSubtotal.toFixed(2)}</span>
            </div>
            <button onClick={() => { setCartOpen(false); navigate("/cart"); }} className="btn-primary w-full" data-testid="cart-checkout">Checkout</button>
            <button onClick={() => setCartOpen(false)} className="w-full text-center overline text-[10px] link-underline mx-auto">Continue Shopping</button>
          </div>
        )}
      </aside>
    </>
  );
}
